import React from "react";
import { Helmet } from "react-helmet";
import Reveal from "../../components/Reveal";
import { useDarkMode } from "../../hooks/useDarkMode";

/* ================= GALLERY DATA ================= */
const galleryImages = [
  { id: 1, img: "/Images/GALLERY1.png", name: "SEO Optimization" },
  { id: 2, img: "/Images/GALLERY2.png", name: "Keyword Research" },
  { id: 3, img: "/Images/GALLERY3.png", name: "Content Strategy" },
  { id: 4, img: "/Images/GALLERY4.png", name: "Website Audit" },
  { id: 5, img: "/Images/GALLERY5.png", name: "Link Building" },
  { id: 6, img: "/Images/GALLERY6.png", name: "Analytics Growth" },
];

const Gallery = () => {
  const { isDarkMode } = useDarkMode();

  /* ================= IMAGE SCHEMA ================= */
  const imageSchema = galleryImages.map((img) => ({
    "@type": "ImageObject",
    contentUrl: `https://novarsis.ai${img.img}`,
    name: img.name,
  }));

  return (
    <>
      <Helmet>
        {/* ===== PRIMARY SEO ===== */}
        <title>SEO Gallery | Seo Up Rank AI SEO Platform</title>

        <meta
          name="description"
          content="Explore the Seo Up Rank gallery showcasing SEO optimization strategies, keyword research, website audits, link building and analytics growth."
        />

        <meta
          name="keywords"
          content="SEO gallery, SEO optimization examples, keyword research gallery, website audit SEO, link building strategy, SEO analytics growth"
        />

        <meta name="author" content="Seo Up Rank" />
        <meta name="robots" content="index, follow, max-image-preview:large" />

        {/* ===== CANONICAL ===== */}
        <link rel="canonical" href="https://novarsis.ai/gallery" />

        {/* ===== OPEN GRAPH ===== */}
        <meta property="og:title" content="SEO Gallery - Seo Up Rank" />
        <meta
          property="og:description"
          content="Discover SEO strategies including optimization, audits, analytics and keyword research."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://novarsis.ai/gallery" />
        <meta
          property="og:image"
          content="https://novarsis.ai/Images/GALLERY1.png"
        />
        <meta property="og:site_name" content="Seo Up Rank" />

        {/* ===== TWITTER SEO ===== */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SEO Gallery - Seo Up Rank" />
        <meta
          name="twitter:description"
          content="Explore SEO optimization, keyword research and website audit strategies."
        />
        <meta
          name="twitter:image"
          content="https://novarsis.ai/Images/GALLERY1.png"
        />

        {/* ===== WEBPAGE SCHEMA ===== */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "SEO Gallery",
            "url": "https://novarsis.ai/gallery",
            "description": "Gallery showcasing SEO optimization, keyword research and analytics growth strategies."
          }
          `}
        </script>

        {/* ===== BREADCRUMB SCHEMA ===== */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://novarsis.ai"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Gallery",
                "item": "https://novarsis.ai/gallery"
              }
            ]
          }
          `}
        </script>

        {/* ===== IMAGE SCHEMA ===== */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ImageGallery",
            name: "SEO Strategy Gallery",
            image: imageSchema,
          })}
        </script>
      </Helmet>

      {/* ================= HERO SECTION ================= */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/Images/GALLERY-BANNER (1).mp4"
          autoPlay
          loop
          muted
          playsInline
          poster="/Images/GAllery-banner.png"
        />

        <div className="absolute inset-0 bg-black/60"></div>

        <div className="relative z-10 text-center px-6 max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-bold text-white">
            SEO <span className="text-[#008300]">Gallery</span>
          </h1>

          <p className="mt-4 text-lg md:text-xl text-gray-200">
            Discover our SEO strategies including optimization, keyword
            research, link building and analytics growth.
          </p>
        </div>
      </section>

      {/* ================= GALLERY SECTION ================= */}
      <section
        aria-label="Seo Up Rank SEO strategies gallery showing optimization techniques"
        className={`relative py-20 px-4 overflow-hidden transition-colors duration-300 ${
          isDarkMode ? "bg-gray-950" : "bg-white"
        }`}
      >
        {/* Hidden SEO Content for crawlers */}
        <p className="sr-only">
          Explore Seo Up Rank SEO optimization gallery featuring keyword
          research, technical SEO audits, link building strategies, website
          performance analysis, and AI-powered SEO automation tools designed to
          improve search rankings and organic traffic growth.
        </p>

        <div className="relative z-10 max-w-7xl mx-auto ">
          {/* Section Heading */}
          <header className="text-center mb-14">
            <h2
              className={`text-3xl md:text-4xl font-bold ${
                isDarkMode ? "text-white" : "text-black"
              }`}
            >
              SEO Optimization <span className="text-[#008300]">Gallery</span>
            </h2>

            <p
              className={`mt-3 max-w-2xl mx-auto ${
                isDarkMode ? "text-gray-400" : "text-gray-600 "
              }`}
            >
              Discover how <strong>Seo Up Rank AI SEO platform</strong> improves
              website rankings through keyword research, technical SEO audits,
              content optimization, and real-time analytics.
            </p>
          </header>

          {/* Gallery Grid */}
          <div
            role="list"
            aria-label="SEO strategy gallery images"
            className="grid grid-cols-1 md:grid-cols-3 gap-10 "
          >
            {galleryImages.map((image) => (
              <Reveal key={image.id}>
                <article role="listitem" className="group">
                  <figure
                    className={`relative w-full h-[340px] rounded-2xl overflow-hidden shadow-xl border transition-all duration-300 ${
                      isDarkMode
                        ? "border-[#008300]/30 shadow-black/60"
                        : "border-[#008300]/20 shadow-gray-300"
                    }`}
                  >
                    {/* Overlay */}
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition"
                    />

                    {/* SEO Optimized Image */}
                    <img
                      src={image.img}
                      alt={`${image.name} SEO strategy and optimization technique`}
                      title={`${image.name} - Seo Up Rank SEO tool`}
                      loading="lazy"
                      width="400"
                      height="340"
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110"
                    />

                    {/* Caption */}
                    <figcaption className="absolute bottom-4 left-4 text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition">
                      {image.name}
                    </figcaption>
                  </figure>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Gallery;
